import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";

import Button from "src/components/Button";
import { getResourseData, mergeWorkRequestData } from "src/components/molecules/WorkRequestListViewContainer/handler";
import { ActiveEmployee, ActiveVendor } from "src/components/molecules/AssignWOResource/types";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { colors } from "src/styles";
import { Ticket } from "src/types";

import { WORK_REQUEST_TYPE } from "../constants";

const BACKEND_TYPES = [
  WORK_REQUEST_TYPE.OPEN,
  WORK_REQUEST_TYPE.APPROVAL_REQUIRED,
  WORK_REQUEST_TYPE.PLANNED,
  WORK_REQUEST_TYPE.COMPLETED,
];

const STATUS_LABELS: Record<string, string> = {
  WAPPR: "Waiting for Approval",
  APPR: "Approved",
  WSCH: "Scheduled",
  INPRG: "In Progress",
  COMP: "Completed",
  CLOSE: "Closed",
  CAN: "Cancelled",
};

const CLOSED_STATUSES = new Set(["completed", "closed", "cancelled", "comp", "close", "can"]);

type Props = {
  embedded?: boolean;
};

type FocusItem = {
  wonum: string;
  headline: string;
  sub: string;
  severity: "critical" | "high" | "normal";
  action: string;
  score: number;
  status: string;
  statusLabel: string;
};

type ConcernItem = {
  wonum: string;
  code: string;
  title: string;
  client: string;
  severity: "critical" | "high" | "normal";
  status: string;
  statusLabel: string;
  ageDays: number;
};

const normalizeStatus = (value?: string) => (value ?? "").toLowerCase().replace(/[_\s-]+/g, "");

const getStatusLabel = (ticket: Ticket) => {
  const code = (ticket.status ?? "").toUpperCase();
  return STATUS_LABELS[code] ?? ticket.statusDescription ?? ticket.status ?? "Unknown";
};

const getPriorityBucket = (priority?: string): "critical" | "high" | "normal" => {
  const value = (priority ?? "").toLowerCase();
  if (value === "emergency" || value === "critical") return "critical";
  if (value === "immediate" || value === "high") return "high";
  return "normal";
};

const getPriorityWeight = (priority?: string) => {
  const bucket = getPriorityBucket(priority);
  if (bucket === "critical") return 100;
  if (bucket === "high") return 50;
  return 10;
};

const getAgeDays = (value?: string) => {
  if (!value) return 0;
  const created = moment(value);
  if (!created.isValid()) return 0;
  return Math.max(0, moment().diff(created, "days"));
};

const isActive = (ticket: Ticket) => {
  const normalized = normalizeStatus(ticket.statusDescription || ticket.status);
  return !CLOSED_STATUSES.has(normalized);
};

const dedupeTickets = (tickets: Ticket[]) => {
  const byWonum = new Map<string, Ticket>();
  tickets.forEach((ticket) => {
    const key = ticket.wonum || ticket.displayWonum || `${ticket.clientId}|${ticket.description}|${ticket.dateCreated}`;
    byWonum.set(key, ticket);
  });
  return Array.from(byWonum.values());
};

const OperationalAwareness = ({ embedded }: Props) => {
  const navigate = useNavigate();
  const { id: loggedInUserId, isManager, vendorId } = useSelector(getUserData);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [employees, setEmployees] = useState<ActiveEmployee[]>([]);
  const [vendors, setVendors] = useState<ActiveVendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setIsLoading(true);
      try {
        const responses = await Promise.all(
          BACKEND_TYPES.map((workRequestType) =>
            FirebaseDataService.getMaximoWorkOrders(
              loggedInUserId,
              {
                pageNum: 1,
                pageSize: 300,
                workRequestType,
              },
              isManager,
              "",
              vendorId || undefined,
            ),
          ),
        );

        const allWorkOrders = responses.flatMap((response) => response.data?.workOrders ?? []);
        const merged = mergeWorkRequestData(dedupeTickets(allWorkOrders));
        const primaryData = responses[0]?.data;

        if (!mounted) return;

        setTickets(merged);
        setEmployees(primaryData?.employees ?? []);
        setVendors(primaryData?.vendors ?? []);
      } catch (error) {
        console.error("Failed to load operational awareness data:", error);
        if (!mounted) return;
        setTickets([]);
        setEmployees([]);
        setVendors([]);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, [isManager, loggedInUserId, vendorId]);

  const derived = useMemo(() => {
    const activeTickets = tickets.filter(isActive);

    const focusItems: FocusItem[] = activeTickets
      .map((ticket) => {
        const assignee =
          getResourseData(ticket.vendorId, ticket.laborId, vendors, employees) ||
          ticket.assignedResourceName ||
          "Unassigned";
        const status = (ticket.status ?? "").toUpperCase();
        const ageDays = getAgeDays(ticket.dateCreated);
        const needsApproval =
          normalizeStatus(ticket.statusDescription || ticket.status).includes("approval") || status === "WAPPR";
        const unassigned = assignee === "Unassigned";
        const priorityBucket = getPriorityBucket(ticket.priority);

        let nextAction = "Open";
        let reason = "Move work forward";
        let score = getPriorityWeight(ticket.priority);

        if (priorityBucket === "critical") {
          nextAction = "Open";
          reason = "Immediate response required";
          score += 40;
        } else if (needsApproval) {
          nextAction = "Review";
          reason = "Pending approval decision";
          score += 30;
        } else if (unassigned) {
          nextAction = "Assign";
          reason = "Dispatch resource";
          score += 20;
        } else if (ageDays >= 7) {
          nextAction = "Escalate";
          reason = `Aging ${ageDays}d in queue`;
          score += 15;
        } else if (status === "INPRG") {
          nextAction = "Check";
          reason = "In progress, awaiting update";
          score += 10;
        }

        return {
          wonum: ticket.wonum,
          headline: ticket.description || ticket.name || ticket.displayWonum || ticket.wonum,
          sub: `${ticket.displayWonum || ticket.wonum} · ${ticket.client || "—"} · ${reason}`,
          severity: priorityBucket,
          action: nextAction,
          score,
          status,
          statusLabel: getStatusLabel(ticket),
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);

    const concernItems: ConcernItem[] = activeTickets
      .map((ticket) => {
        const ageDays = getAgeDays(ticket.dateCreated);
        const priorityBucket = getPriorityBucket(ticket.priority);
        const status = (ticket.status ?? "").toUpperCase();
        const needsApproval =
          normalizeStatus(ticket.statusDescription || ticket.status).includes("approval") || status === "WAPPR";
        const assignee =
          getResourseData(ticket.vendorId, ticket.laborId, vendors, employees) ||
          ticket.assignedResourceName ||
          "Unassigned";
        const isConcern = priorityBucket !== "normal" || needsApproval || assignee === "Unassigned" || ageDays >= 7;

        if (!isConcern) return null;

        return {
          wonum: ticket.wonum,
          code: `OA-${(ticket.displayWonum || ticket.wonum)
            .replace(/[^A-Za-z0-9]/g, "")
            .slice(-4)
            .toUpperCase()}`,
          title: ticket.description || ticket.name || "Work order requires review",
          client: ticket.client || "—",
          severity: priorityBucket,
          status,
          statusLabel: getStatusLabel(ticket),
          ageDays,
        };
      })
      .filter((item): item is ConcernItem => !!item)
      .sort((a, b) => {
        const sevScore = { critical: 3, high: 2, normal: 1 };
        return sevScore[b.severity] - sevScore[a.severity] || b.ageDays - a.ageDays;
      })
      .slice(0, 12);

    const total = tickets.length;
    const active = activeTickets.length;
    const emergency = activeTickets.filter((ticket) => getPriorityBucket(ticket.priority) === "critical").length;
    const unassigned = activeTickets.filter((ticket) => {
      const assignee =
        getResourseData(ticket.vendorId, ticket.laborId, vendors, employees) ||
        ticket.assignedResourceName ||
        "Unassigned";
      return assignee === "Unassigned";
    }).length;
    const openConcerns = concernItems.length;
    const concernRate = total === 0 ? 0 : Math.round((openConcerns / total) * 100);
    const tier = concernRate < 10 ? 0 : concernRate < 25 ? 1 : concernRate < 40 ? 2 : 3;
    const tierLabel = ["Strong", "Stable", "Watch", "At Risk"][tier];

    return {
      total,
      active,
      emergency,
      unassigned,
      openConcerns,
      concernRate,
      tier: tier as 0 | 1 | 2 | 3,
      tierLabel,
      focusItems,
      concernItems,
    };
  }, [employees, tickets, vendors]);

  const content = (
    <>
      <PageHeader>
        <Eyebrow>01 / Overview</Eyebrow>
        <Title>Operational Awareness</Title>
        <Meta>State of all work moving through the system.</Meta>
      </PageHeader>

      <TilesGrid>
        <SummaryTile>
          <SummaryEyebrow>Active Work</SummaryEyebrow>
          <SummaryValue>{isLoading ? "—" : derived.active}</SummaryValue>
          <SummaryMeta>of {derived.total} total</SummaryMeta>
        </SummaryTile>
        <SummaryTile>
          <SummaryEyebrow>Emergency</SummaryEyebrow>
          <SummaryValue>{isLoading ? "—" : derived.emergency}</SummaryValue>
          <SummaryMeta>{derived.emergency > 0 ? "requiring immediate attention" : "none"}</SummaryMeta>
        </SummaryTile>
        <SummaryTile>
          <SummaryEyebrow>Unassigned</SummaryEyebrow>
          <SummaryValue>{isLoading ? "—" : derived.unassigned}</SummaryValue>
          <SummaryMeta>pending dispatch</SummaryMeta>
        </SummaryTile>
        <SummaryTile>
          <SummaryEyebrow>Concern Rate</SummaryEyebrow>
          <SummaryValue>{isLoading ? "—" : `${derived.concernRate}%`}</SummaryValue>
          <SummaryMeta>{derived.tierLabel}</SummaryMeta>
          <MiniBar>
            {[0, 1, 2, 3].map((index) => (
              <MiniBarSegment key={index} $active={index === derived.tier} />
            ))}
          </MiniBar>
        </SummaryTile>
        <SummaryTile>
          <SummaryEyebrow>Open Service Concerns</SummaryEyebrow>
          <SummaryValue>{isLoading ? "—" : derived.openConcerns}</SummaryValue>
          <SummaryMeta>ranked for attention</SummaryMeta>
        </SummaryTile>
      </TilesGrid>

      <Panel>
        <PanelHeader>
          <PanelTitle>Focus</PanelTitle>
          <PanelMeta>
            {isLoading
              ? "Loading…"
              : derived.focusItems.length === 0
                ? "Nothing requires attention"
                : `${derived.focusItems.length} items · ranked by urgency`}
          </PanelMeta>
        </PanelHeader>

        {isLoading ? (
          <LoadingRow>
            <Spinner animation="border" size="sm" />
          </LoadingRow>
        ) : !derived.total ? (
          <EmptyState>
            <EmptyTitle>No work requests yet.</EmptyTitle>
            <EmptyCopy>Create the first request to begin tracking work and operational risks.</EmptyCopy>
            <Button text="New Request" onClick={() => navigate("/workrequests/create")} />
          </EmptyState>
        ) : !derived.focusItems.length ? (
          <EmptyRow>Nothing requires attention.</EmptyRow>
        ) : (
          derived.focusItems.map((item) => (
            <FocusRow key={item.wonum} onClick={() => navigate(`/workrequests/${item.wonum}`)}>
              <SeverityPill $severity={item.severity}>
                {item.severity === "critical" ? "CRITICAL" : item.severity === "high" ? "HIGH" : "NORMAL"}
              </SeverityPill>
              <FocusText>
                <FocusHeadline>{item.headline}</FocusHeadline>
                <FocusSub>{item.sub}</FocusSub>
              </FocusText>
              <FocusButton
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/workrequests/${item.wonum}`);
                }}
              >
                {item.action}
              </FocusButton>
              <Arrow>→</Arrow>
            </FocusRow>
          ))
        )}
      </Panel>

      <Panel style={{ marginTop: 24 }}>
        <PanelHeader>
          <PanelTitle>Open Service Concerns</PanelTitle>
          <PanelMeta>
            {isLoading
              ? "Loading…"
              : derived.openConcerns === 0
                ? "No open concerns"
                : `${derived.openConcerns} open · Operational Risk`}
          </PanelMeta>
        </PanelHeader>

        {isLoading ? (
          <LoadingRow>
            <Spinner animation="border" size="sm" />
          </LoadingRow>
        ) : !derived.concernItems.length ? (
          <EmptyRow>No open concerns.</EmptyRow>
        ) : (
          <>
            <ConcernHeader>
              <span>WR</span>
              <span>OA</span>
              <span>Title</span>
              <span>Client</span>
              <RightLabel>Severity</RightLabel>
              <RightLabel>Status</RightLabel>
              <RightLabel>Age</RightLabel>
            </ConcernHeader>
            {derived.concernItems.map((item) => (
              <ConcernRow key={item.code} onClick={() => navigate(`/workrequests/${item.wonum}`)}>
                <IdTag>{item.wonum}</IdTag>
                <IdTag>{item.code}</IdTag>
                <ConcernTitle>{item.title}</ConcernTitle>
                <ConcernClient>{item.client}</ConcernClient>
                <RightCell>
                  <SeverityBadge $severity={item.severity}>{item.severity}</SeverityBadge>
                </RightCell>
                <RightCell>
                  <StatusText>{item.statusLabel}</StatusText>
                </RightCell>
                <RightCell>
                  <AgeText>{item.ageDays}d</AgeText>
                </RightCell>
              </ConcernRow>
            ))}
          </>
        )}
      </Panel>
    </>
  );

  if (embedded) {
    return <EmbeddedContainer>{content}</EmbeddedContainer>;
  }

  return <StandaloneContainer>{content}</StandaloneContainer>;
};

export default OperationalAwareness;

const StandaloneContainer = styled.div`
  padding: 24px 40px 0;
`;

const EmbeddedContainer = styled.div``;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: ${colors.grey.dark6};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 6px 0 0;
  color: ${colors.black.dark1};
  font-size: 26px;
  font-weight: 600;
`;

const Meta = styled.p`
  margin: 4px 0 0;
  color: ${colors.grey.dark6};
  font-size: 13px;
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryTile = styled.div`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
  padding: 16px;
`;

const SummaryEyebrow = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.grey.dark6};
`;

const SummaryValue = styled.div`
  margin-top: 10px;
  color: ${colors.black.dark1};
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
`;

const SummaryMeta = styled.div`
  margin-top: 8px;
  color: ${colors.grey.dark6};
  font-size: 10px;
`;

const MiniBar = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 10px;
`;

const MiniBarSegment = styled.span<{ $active: boolean }>`
  display: block;
  width: 12px;
  height: 6px;
  background: ${({ $active }) => ($active ? colors.black.dark1 : colors.white.default)};
  border: 1px solid ${colors.grey.light4};
`;

const Panel = styled.section`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.white.default};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.grey.light3};
`;

const PanelTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.black.dark1};
`;

const PanelMeta = styled.span`
  font-size: 10px;
  color: ${colors.grey.dark6};
`;

const LoadingRow = styled.div`
  padding: 28px 16px;
  display: flex;
  justify-content: center;
`;

const EmptyRow = styled.div`
  padding: 24px 16px;
  color: ${colors.grey.dark6};
  font-size: 12px;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 28px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const EmptyTitle = styled.div`
  color: ${colors.black.dark1};
  font-size: 13px;
  font-weight: 600;
`;

const EmptyCopy = styled.div`
  color: ${colors.grey.dark6};
  font-size: 12px;
  margin-bottom: 8px;
`;

const FocusRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr auto 24px;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-top: 1px solid ${colors.grey.light3};
  cursor: pointer;

  &:hover {
    background: ${colors.grey.light6};
  }
`;

const SeverityPill = styled.span<{ $severity: "critical" | "high" | "normal" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 3px 8px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid ${colors.black.dark1};
  background: ${({ $severity }) => ($severity === "critical" ? colors.black.dark1 : colors.white.default)};
  color: ${({ $severity }) => ($severity === "critical" ? colors.white.default : colors.black.dark1)};
`;

const FocusText = styled.div`
  min-width: 0;
`;

const FocusHeadline = styled.div`
  color: ${colors.black.dark1};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FocusSub = styled.div`
  margin-top: 2px;
  color: ${colors.grey.dark6};
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FocusButton = styled.button`
  border: 1px solid ${colors.black.dark1};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
`;

const Arrow = styled.span`
  color: ${colors.black.dark1};
  font-size: 16px;
  line-height: 1;
`;

const ConcernHeader = styled.div`
  display: grid;
  grid-template-columns: 90px 90px 1fr 180px 140px 120px 110px;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.grey.light3};
  color: ${colors.grey.dark6};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const ConcernRow = styled.div`
  display: grid;
  grid-template-columns: 90px 90px 1fr 180px 140px 120px 110px;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid ${colors.grey.light3};
  cursor: pointer;

  &:hover {
    background: ${colors.grey.light6};
  }
`;

const IdTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 4px 8px;
  border: 1px solid ${colors.grey.light4};
  background: ${colors.grey.light6};
  color: ${colors.black.light1};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ConcernTitle = styled.span`
  color: ${colors.black.dark1};
  font-size: 13px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ConcernClient = styled.span`
  color: ${colors.grey.dark6};
  font-size: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const RightLabel = styled.span`
  text-align: right;
`;

const RightCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SeverityBadge = styled.span<{ $severity: "critical" | "high" | "normal" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 4px 8px;
  border: 1px solid ${colors.black.dark1};
  background: ${({ $severity }) => ($severity === "critical" ? colors.black.dark1 : colors.white.default)};
  color: ${({ $severity }) => ($severity === "critical" ? colors.white.default : colors.black.dark1)};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const StatusText = styled.span`
  color: ${colors.black.dark1};
  font-size: 12px;
`;

const AgeText = styled.span`
  color: ${colors.grey.dark6};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;
